import React, { useEffect } from 'react';
import {
    usePermissions,
    SimpleForm,
    required,
    ReferenceInput,
    ReferenceArrayInput,
    AutocompleteInput,
    AutocompleteArrayInput,
    useGetOne,
    useRecordContext,
    useSaveContext,
    useTranslate,
    SelectInput,
} from 'react-admin';
import { DateInput } from '@/components/global/inputs/DateInput.jsx';
import { Box } from '@mui/material';
import { serviceRequestPriorityChoices, serviceRequestStatusChoices, serviceRequestTypeChoices } from '@/components/global/options/selectOptions.jsx';
import { EditToolbar } from '@/components/global/buttons/toolbar.jsx';
import { billOfMaterialsModuleFilterToQuery, deviceFilterToQuery, extendedCollectionPointFilterToQuery } from '@/helpers/filters/filterToQuery.js';
import { useFormContext, useWatch } from 'react-hook-form';
import SelectWithIcon from '@/components/global/inputs/SelectWithIcon';
import { CustomRichText } from '@/components/global/inputs/CustomRichText';
import { Palette } from '@/themes/themeOverrides/Palette';
import PropTypes from 'prop-types';

const CollectionPointInput = () => {
    const translate = useTranslate();
    const record = useRecordContext();
    const { setValue } = useFormContext();

    const getCollectionPointOptionText = choice => {
        return `${choice.customId}, ${choice.city}, ${choice.address}, ${choice.postalCode} ${choice.city}`;
    };

    const handleChange = () => {
        setValue('deviceId', null);
        setValue('coordinatorId', null);
        setValue('servicemanId', null);
    };

    return (
        <Box className='autocomplete-parent'>
            <ReferenceInput source='collectionPointId' reference='collection-point' perPage={25}>
                <AutocompleteInput
                    filterToQuery={extendedCollectionPointFilterToQuery}
                    label={translate('common.collectionPoint')}
                    readOnly={!!record?.collectionPoint?.id}
                    onChange={handleChange}
                    validate={required(translate('common.validation.required'))}
                    fullWidth
                    optionText={getCollectionPointOptionText}
                    noOptionsText={translate('resources.serviceRequest.labels.noCollectionPointFound')}
                    disablePortal={true}
                    sx={{
                        paddingTop: '1px',
                        '& .Mui-focused': {
                            '& .MuiButtonBase-root.MuiAutocomplete-popupIndicator.MuiAutocomplete-popupIndicator': {
                                transform: 'rotate(0)',
                            },
                        },
                        '& .MuiButtonBase-root.MuiAutocomplete-popupIndicator.MuiAutocomplete-popupIndicator': {
                            backgroundImage: `url("data:image/svg+xml,%3Csvg width='20' height='20' viewBox='0 0 20 20' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cg id='icon'%3E%3Cpath id='Vector 16' d='M16 16L11 11' stroke='%231A1A1A' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3Ccircle id='Ellipse 7' cx='9' cy='9' r='5' fill='white' stroke='%231A1A1A' stroke-width='1.6' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/g%3E%3C/svg%3E%0A")`,
                        },
                    }}
                />
            </ReferenceInput>
        </Box>
    );
};

const DeviceInput = () => {
    const translate = useTranslate();
    const record = useRecordContext();
    const collectionPointId = useWatch({ name: 'collectionPointId' });
    const filter = { deploymentStatus: 'deployed', collectionPointId: collectionPointId };

    return (
        <Box className='autocomplete-parent'>
            <ReferenceInput source='deviceId' reference='device' perPage={25} filter={filter} enableGetChoices={() => !!collectionPointId}>
                <AutocompleteInput
                    filterToQuery={deviceFilterToQuery}
                    label={translate('resources.serviceRequest.labels.device')}
                    readOnly={!collectionPointId || !!record?.deviceId}
                    validate={required(translate('common.validation.required'))}
                    fullWidth
                    noOptionsText={translate('resources.serviceRequest.labels.noDevicesInCollectionPoint')}
                    disablePortal={true}
                />
            </ReferenceInput>
        </Box>
    );
};

export const CoordinatorInput = ({ label }) => {
    const record = useRecordContext();
    const formCollectionPointId = useWatch({ name: 'collectionPointId' });
    const recordCollectionPointId = record?.collectionPoint?.id;
    const collectionPointId = formCollectionPointId ?? recordCollectionPointId;
    const { data, isLoading } = useGetOne('collection-point', { id: collectionPointId }, { enabled: !!collectionPointId });
    const filter = { role: 'ROLE_SERVICE_MASTER', serviceCompanyId: data?.serviceCompanyId };

    return (
        <Box className='autocomplete-parent'>
            <ReferenceInput source='coordinatorId' reference='user' perPage={25} filter={filter} enableGetChoices={() => !!data?.serviceCompanyId}>
                <AutocompleteInput
                    filterToQuery={deviceFilterToQuery}
                    label={label}
                    readOnly={!collectionPointId}
                    isLoading={isLoading}
                    fullWidth
                    noOptionsText={useTranslate()('resources.serviceRequest.labels.noCoordinator')}
                    disablePortal={true}
                />
            </ReferenceInput>
        </Box>
    );
};

export const ServicemanInput = ({ label }) => {
    const record = useRecordContext();
    const formCollectionPointId = useWatch({ name: 'collectionPointId' });
    const recordCollectionPointId = record?.collectionPoint?.id;
    const collectionPointId = formCollectionPointId ?? recordCollectionPointId;
    const { data, isLoading } = useGetOne('collection-point', { id: collectionPointId }, { enabled: !!collectionPointId });
    const filter = { role: 'ROLE_SERVICE', serviceCompanyId: data?.serviceCompanyId };

    return (
        <Box className='autocomplete-parent'>
            <ReferenceInput source='servicemanId' reference='user' perPage={25} filter={filter} enableGetChoices={() => !!data?.serviceCompanyId}>
                <AutocompleteInput
                    filterToQuery={deviceFilterToQuery}
                    label={label}
                    readOnly={!collectionPointId}
                    isLoading={isLoading}
                    fullWidth
                    noOptionsText={useTranslate()('resources.serviceRequest.labels.noServiceman')}
                    disablePortal={true}
                />
            </ReferenceInput>
        </Box>
    );
};

export const UsedPartsInput = ({ label }) => {
    const translate = useTranslate();

    return (
        <Box className='autocomplete-parent'>
            <ReferenceArrayInput source='usedPartIds' reference='spare-part' perPage={25} filter={{ status: 'inUse' }}>
                <AutocompleteArrayInput
                    className='custom-select-array-input'
                    label={label}
                    filterToQuery={billOfMaterialsModuleFilterToQuery}
                    optionText={choice => choice.name}
                    fullWidth
                    noOptionsText={translate('resources.serviceRequest.labels.noSpareParts')}
                    disablePortal={true}
                    sx={{
                        '& .MuiInputBase-root': {
                            width: '100%',
                            gap: '2px',
                            '& .MuiAutocomplete-endAdornment': {
                                right: '14px',
                                top: 'calc(50% - 16px)',
                            },
                        },
                        '& .MuiButtonBase-root': {
                            color: Palette.black,
                            fontWeight: 400,
                            borderRadius: '8px',
                            margin: '0',
                            '& .MuiChip-label': {
                                padding: '0 8px',
                                maxWidth: '160px',
                            },
                        },
                        '& .MuiButtonBase-root.MuiIconButton-root': {
                            backgroundImage: `url("data:image/svg+xml,%3Csvg width='20' height='20' viewBox='0 0 20 20' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cg id='icon'%3E%3Cpath id='Vector 16' d='M16 16L11 11' stroke='%231A1A1A' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3Ccircle id='Ellipse 7' cx='9' cy='9' r='5' fill='white' stroke='%231A1A1A' stroke-width='1.6' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/g%3E%3C/svg%3E%0A")`,
                        },
                        '& .Mui-focused': {
                            '& .MuiButtonBase-root.MuiAutocomplete-popupIndicator.MuiAutocomplete-popupIndicator': {
                                transform: 'rotate(0)',
                            },
                        },
                    }}
                />
            </ReferenceArrayInput>
        </Box>
    );
};

const ServiceInspectionTypeInput = () => {
    const translate = useTranslate();
    const { setValue } = useFormContext();
    const record = useRecordContext();
    const deviceId = useWatch({ name: 'deviceId' });
    const { data: deviceData } = useGetOne('device', { id: deviceId }, { enabled: !!deviceId });

    useEffect(() => {
        if (record?.type) return;
        if (!deviceData?.deploymentDate) {
            setValue('type', null, { shouldDirty: true, shouldValidate: true });
            return;
        }
        const deploymentDate = new Date(deviceData.deploymentDate);

        if (Number.isNaN(deploymentDate.getTime())) {
            setValue('type', null, { shouldDirty: true, shouldValidate: true });
            return;
        }

        const warrantyUntil = new Date(deploymentDate);
        warrantyUntil.setFullYear(warrantyUntil.getFullYear() + 2);

        const today = new Date();
        const isWarranty = today <= warrantyUntil;

        setValue('type', isWarranty ? 'warranty' : 'postWarranty', { shouldDirty: true, shouldValidate: true });
    }, [deviceData?.deploymentDate, record?.type, setValue]);

    const inspectionTypeChoices = [
        { id: 'warranty', name: translate('resources.serviceInspection.labels.warranty') },
        { id: 'postWarranty', name: translate('resources.serviceInspection.labels.postWarranty') },
    ];

    return (
        <Box className='autocomplete-parent'>
            <SelectInput source='type' label={translate('resources.serviceInspection.labels.type')} choices={inspectionTypeChoices} fullWidth disabled />
        </Box>
    );
};

export const ServiceForm = ({ resource }) => {
    const { isLoading, permissions } = usePermissions();
    const { saving } = useSaveContext();
    const translate = useTranslate();

    const defaultStatus = permissions.isServiceMaster ? 'accepted' : 'new';

    if (isLoading) return null;

    return (
        <SimpleForm
            toolbar={
                <EditToolbar
                    alwaysEnableSave
                    saving={saving}
                    label={
                        resource === 'service-inspection'
                            ? translate('resources.serviceRequest.labels.createServiceInspection')
                            : translate('resources.serviceRequest.labels.createServiceRequest')
                    }
                    icon={null}
                    sx={{ marginTop: '32px' }}
                />
            }
            sx={{
                '& .MuiFormLabel-filled': {
                    fontSize: '11px',
                },
                '& .MuiFormHelperText-root': {
                    display: 'none',
                },
                '& .autocomplete-parent': {
                    '& + .autocomplete-parent': {
                        marginTop: '28px',
                    },
                    '& .ra-input-type': {
                        '& .MuiFormLabel-root': {
                            color: `${Palette.gray.gray400} !important`,
                        },
                        '& .MuiInputBase-root.Mui-disabled': {
                            backgroundColor: Palette.white,
                            '& .MuiSelect-select': {
                                fontWeight: '400 !important',
                                WebkitTextFillColor: `${Palette.gray.gray400} !important`,
                            },
                        },
                    },
                },
                '& .select-with-icon': {
                    marginTop: '32px',
                },
                '& .rich-text-input': {
                    margin: '32px 0 0',
                    '& .RaLabeled-label': {
                        display: 'block',
                        position: 'absolute',
                        zIndex: 2,
                        backgroundColor: Palette.white,
                        padding: '0 5px',
                        top: '-8px',
                        left: '10px',
                        fontSize: '11px',
                    },
                },
                '& .MuiInputLabel-root.MuiFormLabel-filled': {
                    color: `${Palette.gray.gray900} !important`,
                },
                '& .MuiAutocomplete-popper': {
                    backgroundColor: Palette.white,
                    padding: '4px 0',
                    boxShadow: Palette.boxShadow.elevation8,
                    borderRadius: '12px',
                    '& ul': {
                        padding: 0,
                    },
                    '& .MuiAutocomplete-noOptions': {
                        margin: '0 16px',
                    },
                },
                '& .date-input': {
                    marginTop: '30px',
                },
            }}
        >
            <CollectionPointInput />
            <DeviceInput />
            {resource === 'service-inspection' && <ServiceInspectionTypeInput />}
            <CoordinatorInput label={translate('resources.serviceRequest.labels.coordinator')} />
            <ServicemanInput label={translate('resources.serviceRequest.labels.serviceman')} />
            <UsedPartsInput label={translate('resources.serviceRequest.labels.usedParts')} />
            <SelectWithIcon
                source='status'
                label={translate('common.status')}
                className='select-with-icon'
                colorChoices={serviceRequestStatusChoices(translate)}
                defaultValue={defaultStatus}
                validate={required(translate('common.validation.required'))}
                fullWidth
            />
            <SelectWithIcon
                source='priority'
                label={translate('common.priorityLabel')}
                className='select-with-icon select-with-icon--outlined'
                colorChoices={serviceRequestPriorityChoices(translate)}
                defaultValue='normal'
                validate={required(translate('common.validation.required'))}
                fullWidth
            />
            {resource !== 'service-inspection' && (
                <SelectWithIcon
                    source='type'
                    label={translate('resources.serviceRequest.labels.type')}
                    className='select-with-icon select-with-icon--outlined'
                    colorChoices={serviceRequestTypeChoices(translate)}
                    defaultValue='mechanicalFailure'
                    validate={required(translate('common.validation.required'))}
                    fullWidth
                />
            )}
            {resource === 'service-inspection' && (
                <DateInput className='date-input' source='scheduledDate' label={translate('resources.serviceInspection.labels.plannedDate')} fullWidth={true} />
            )}
            <CustomRichText source='description' label={translate('resources.serviceRequest.activityList.description')} fullWidth />
        </SimpleForm>
    );
};

ServiceForm.propTypes = {
    resource: PropTypes.string,
};

CoordinatorInput.propTypes = {
    label: PropTypes.string,
};

ServicemanInput.propTypes = {
    label: PropTypes.string,
};

UsedPartsInput.propTypes = {
    label: PropTypes.string,
};
